import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import TradingViewLink from '../components/TradingViewLink';
import { 
  TrendingUp, TrendingDown, DollarSign, Target, BarChart3, 
  RefreshCw, ChevronDown, ChevronUp, AlertTriangle, CheckCircle,
  Info, Zap, Shield, Calendar, Plus, ArrowDownCircle, ArrowUpCircle,
  Settings, PiggyBank, LineChart, Layers, Trash2, Bell, Clock, History, Star
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { getAssetName } from '../utils/assetNames';
import OnboardingTutorial from '../components/OnboardingTutorial';
import { DCA_SLIDES } from '../config/tutorialSlides';
import HowItWorks from '../components/HowItWorks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LocalStorage key for guest watchlist (same as Watchlist.js)
const LOCAL_WATCHLIST_KEY = 'tradex_guest_watchlist';

const DCA = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('setup');
  const [zoneHistory, setZoneHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    symbol: '',
    customSymbol: '',
    totalBudget: 10000,
    classicPercent: 50,
    frequency: 'weekly',
    referenceType: 'local_high'
  });
  
  // Custom tiers
  const [customTiers, setCustomTiers] = useState([
    { drop_percent: 5, multiplier: 1.5 },
    { drop_percent: 10, multiplier: 2.0 },
    { drop_percent: 15, multiplier: 3.0 },
    { drop_percent: 20, multiplier: 4.0 },
    { drop_percent: 30, multiplier: 5.0 },
  ]);

  // Get watchlist from localStorage for guests
  const getLocalWatchlist = () => {
    try {
      const saved = localStorage.getItem(LOCAL_WATCHLIST_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse local watchlist:', e);
    }
    // Default watchlist
    return [
      { symbol: 'BINANCE:BTCUSDT', name: 'Bitcoin', category: 'crypto' },
      { symbol: 'FX:EURUSD', name: 'EUR/USD', category: 'forex' },
      { symbol: 'NASDAQ:AAPL', name: 'Apple Inc.', category: 'stocks' }
    ];
  };

  // Fetch watchlist
  const fetchWatchlist = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const response = await axios.get(`${API}/watchlist/symbols`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWatchlist(response.data.symbols || []);
      } catch (error) {
        console.error('Error fetching watchlist:', error);
        setWatchlist(getLocalWatchlist());
      }
    } else {
      // Guest: load from localStorage
      setWatchlist(getLocalWatchlist());
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  // Fetch zone history
  const fetchZoneHistory = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/dca/zone-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZoneHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching zone history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    fetchZoneHistory();
  }, [fetchZoneHistory]);

  // Analyze DCA
  const analyzeDCA = async () => {
    const symbol = formData.symbol === 'custom' ? formData.customSymbol : formData.symbol;
    if (!symbol) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/dca/analyze`, {
        symbol: symbol,
        total_budget: formData.totalBudget,
        classic_percent: formData.classicPercent,
        improved_percent: 100 - formData.classicPercent,
        frequency: formData.frequency,
        reference_type: formData.referenceType,
        scaling_tiers: customTiers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAnalysis(response.data);
      setActiveTab('analysis');
    } catch (error) {
      console.error('Error analyzing DCA:', error);
      alert('Erreur lors de l\'analyse DCA: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Update tier
  const updateTier = (index, field, value) => {
    const newTiers = [...customTiers];
    newTiers[index][field] = parseFloat(value) || 0;
    setCustomTiers(newTiers);
  };

  // Add tier
  const addTier = () => {
    const lastTier = customTiers[customTiers.length - 1];
    setCustomTiers([...customTiers, {
      drop_percent: lastTier.drop_percent + 10,
      multiplier: lastTier.multiplier + 1
    }]);
  };

  // Remove tier
  const removeTier = (index) => {
    if (customTiers.length > 1) {
      setCustomTiers(customTiers.filter((_, i) => i !== index));
    }
  };

  // Save DCA plan
  const [savingPlan, setSavingPlan] = useState(false);
  const [savedPlans, setSavedPlans] = useState([]);

  const saveDCAPlan = async () => {
    if (!analysis || !isAuthenticated) {
      if (!isAuthenticated) {
        navigate('/subscribe');
      }
      return;
    }

    setSavingPlan(true);
    try {
      const planData = {
        symbol: analysis.symbol,
        total_budget: analysis.total_budget,
        classic_budget: analysis.classic_budget,
        improved_budget: analysis.improved_budget,
        frequency: analysis.frequency,
        base_amount: analysis.base_amount,
        scaling_tiers: customTiers,
        reference_type: formData.referenceType,
        current_price: analysis.current_price,
        buy_zones: analysis.buy_zones || [],
        ai_analysis: analysis.ai_analysis,
        market_bias: analysis.market_bias,
        classic_schedule: analysis.classic_schedule || [],
        improved_schedule: analysis.improved_schedule || [],
        classic_avg_price: analysis.classic_avg_price,
        improved_avg_price: analysis.improved_avg_price,
        potential_savings_percent: analysis.potential_savings_percent
      };

      await axios.post(`${API}/dca/plans`, planData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Plan DCA sauvegardé avec succès !');
      fetchSavedPlans();
      setActiveTab('tracking');
    } catch (error) {
      console.error('Error saving DCA plan:', error);
      alert('❌ Erreur lors de la sauvegarde: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingPlan(false);
    }
  };

  // Fetch saved plans
  const fetchSavedPlans = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/dca/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching DCA plans:', error);
    }
  }, [token, isAuthenticated]);

  // Delete a plan
  const [deletingPlanId, setDeletingPlanId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  
  const handleDeleteClick = (planId) => {
    setConfirmDeleteId(planId);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    
    setDeletingPlanId(confirmDeleteId);
    try {
      await axios.delete(`${API}/dca/plans/${confirmDeleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state immediately
      setSavedPlans(prev => prev.filter(p => p.id !== confirmDeleteId));
      setConfirmDeleteId(null);
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Erreur lors de la suppression: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingPlanId(null);
    }
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };

  // Tracking state
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [planEntries, setPlanEntries] = useState([]);
  const [planSummary, setPlanSummary] = useState(null);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [addingEntry, setAddingEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    amount: '',
    price: '',
    entry_type: 'classic',
    notes: ''
  });

  // Get selected plan details
  const selectedPlan = savedPlans.find(p => p.id === selectedPlanId);

  // Fetch entries for selected plan
  const fetchPlanEntries = useCallback(async (planId) => {
    if (!planId || !isAuthenticated) return;
    
    setLoadingEntries(true);
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/dca/entries/${planId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/dca/summary/${planId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPlanEntries(entriesRes.data.entries || []);
      setPlanSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching plan data:', error);
      setPlanEntries([]);
      setPlanSummary(null);
    } finally {
      setLoadingEntries(false);
    }
  }, [token, isAuthenticated]);

  // When selected plan changes
  useEffect(() => {
    if (selectedPlanId) {
      fetchPlanEntries(selectedPlanId);
    }
  }, [selectedPlanId, fetchPlanEntries]);

  // Auto-select first plan
  useEffect(() => {
    if (savedPlans.length > 0 && !selectedPlanId) {
      setSelectedPlanId(savedPlans[0].id);
    }
  }, [savedPlans, selectedPlanId]);

  // Add new entry
  const handleAddEntry = async () => {
    if (!selectedPlanId || !newEntry.amount || !newEntry.price) return;
    
    setAddingEntry(true);
    try {
      await axios.post(`${API}/dca/entries`, {
        plan_id: selectedPlanId,
        entry_type: newEntry.entry_type,
        amount: parseFloat(newEntry.amount),
        price: parseFloat(newEntry.price),
        entry_date: newEntry.entry_date,
        notes: newEntry.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reset form
      setNewEntry({
        entry_date: new Date().toISOString().split('T')[0],
        amount: '',
        price: '',
        entry_type: 'classic',
        notes: ''
      });
      
      // Refresh entries
      fetchPlanEntries(selectedPlanId);
    } catch (error) {
      console.error('Error adding entry:', error);
      alert('Erreur: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAddingEntry(false);
    }
  };

  // Delete entry
  const [deletingEntryId, setDeletingEntryId] = useState(null);
  
  const handleDeleteEntry = async (entryId) => {
    setDeletingEntryId(entryId);
    try {
      await axios.delete(`${API}/dca/entries/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPlanEntries(selectedPlanId);
    } catch (error) {
      console.error('Error deleting entry:', error);
    } finally {
      setDeletingEntryId(null);
    }
  };

  useEffect(() => {
    fetchSavedPlans();
  }, [fetchSavedPlans]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="dca-page">
      {/* Onboarding Tutorial */}
      <OnboardingTutorial
        pageId="dca"
        slides={DCA_SLIDES}
        storageKey="tutorial_dca_seen"
      />

      {/* Header */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <PiggyBank className="w-8 h-8 text-primary" />
              DCA Amélioré
            </h1>
            <HowItWorks pageKey="dca" />
          </div>
          <p className="text-muted-foreground">
            <strong>DCA (Dollar Cost Averaging)</strong> - Stratégie d&apos;investissement progressif avec analyse IA des zones d&apos;entrée optimales
          </p>
        </div>
        <TradingViewLink symbol={formData.symbol} label="TradingView" />
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-500/10 border-blue-500/20 mb-6">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-400 mb-1">Qu'est-ce que le DCA (Dollar Cost Averaging) ?</h3>
              <p className="text-sm text-muted-foreground">
                Le <strong>DCA classique</strong> (investissement programmé) consiste à investir un montant fixe à intervalles réguliers, 
                quel que soit le prix du marché. Le <strong>DCA amélioré</strong> va plus loin en augmentant les montants investis 
                quand le prix atteint des zones techniquement attractives (supports, Order Blocks, FVG), 
                permettant de réduire significativement ton prix moyen d'entrée.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="setup" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="analysis" className="flex items-center gap-2" disabled={!analysis}>
            <BarChart3 className="w-4 h-4" />
            Analyse
          </TabsTrigger>
          <TabsTrigger value="tracking" className="flex items-center gap-2">
            <LineChart className="w-4 h-4" />
            Suivi
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Alertes {zoneHistory.length > 0 && <Badge variant="secondary" className="ml-1 text-xs">{zoneHistory.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Setup Tab */}
        <TabsContent value="setup">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Configuration Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Paramètres DCA
                </CardTitle>
                <CardDescription>Configure ta stratégie d'investissement</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Symbol Selection */}
                <div className="space-y-2">
                  <Label>Actif</Label>
                  <Select 
                    value={formData.symbol} 
                    onValueChange={(v) => setFormData({...formData, symbol: v})}
                  >
                    <SelectTrigger data-testid="dca-symbol-select">
                      <SelectValue placeholder="Sélectionne un actif" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BTC/USDT">Bitcoin (BTC/USDT)</SelectItem>
                      <SelectItem value="ETH/USDT">Ethereum (ETH/USDT)</SelectItem>
                      {/* Group watchlist by category and sort by name */}
                      {watchlist
                        .map(item => ({
                          ...item,
                          displayName: getAssetName(item.symbol.replace(/^[A-Z]+:/, '')) || item.name || item.symbol,
                          cleanSymbol: item.symbol.replace(/^[A-Z]+:/, '')
                        }))
                        .sort((a, b) => {
                          // Sort by category first, then by name
                          const catOrder = { crypto: 1, stocks: 2, indices: 3, commodities: 4, forex: 5, etf: 6 };
                          const catA = catOrder[a.category] || 99;
                          const catB = catOrder[b.category] || 99;
                          if (catA !== catB) return catA - catB;
                          return a.displayName.localeCompare(b.displayName);
                        })
                        .map((item) => (
                          <SelectItem key={item.symbol} value={item.cleanSymbol}>
                            {item.displayName} ({item.cleanSymbol})
                          </SelectItem>
                        ))
                      }
                      <SelectItem value="custom">Autre (saisie manuelle)</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.symbol === 'custom' && (
                    <Input
                      placeholder="Ex: AAPL, SOL/USDT, EUR/USD"
                      value={formData.customSymbol}
                      onChange={(e) => setFormData({...formData, customSymbol: e.target.value.toUpperCase()})}
                      className="mt-2"
                      data-testid="dca-custom-symbol"
                    />
                  )}
                </div>

                {/* Total Budget */}
                <div className="space-y-2">
                  <Label>Budget Total ($)</Label>
                  <Input
                    type="number"
                    value={formData.totalBudget}
                    onChange={(e) => setFormData({...formData, totalBudget: parseFloat(e.target.value) || 0})}
                    data-testid="dca-budget"
                  />
                </div>

                {/* Classic vs Improved Split */}
                <div className="space-y-4">
                  <Label>Répartition DCA Classique / Amélioré</Label>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground w-20">Classique</span>
                    <Slider
                      value={[formData.classicPercent]}
                      onValueChange={(v) => setFormData({...formData, classicPercent: v[0]})}
                      max={100}
                      step={5}
                      className="flex-1"
                    />
                    <span className="text-sm text-muted-foreground w-20 text-right">Amélioré</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-400">{formData.classicPercent}% (${(formData.totalBudget * formData.classicPercent / 100).toFixed(0)})</span>
                    <span className="text-green-400">{100 - formData.classicPercent}% (${(formData.totalBudget * (100 - formData.classicPercent) / 100).toFixed(0)})</span>
                  </div>
                </div>

                {/* Frequency */}
                <div className="space-y-2">
                  <Label>Fréquence (DCA Classique)</Label>
                  <Select 
                    value={formData.frequency} 
                    onValueChange={(v) => setFormData({...formData, frequency: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Journalier</SelectItem>
                      <SelectItem value="weekly">Hebdomadaire</SelectItem>
                      <SelectItem value="monthly">Mensuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Analyze Button */}
                <Button 
                  className="w-full" 
                  onClick={() => {
                    if (!isAuthenticated) {
                      navigate('/subscribe');
                    } else {
                      analyzeDCA();
                    }
                  }}
                  disabled={loading || (!formData.symbol || (formData.symbol === 'custom' && !formData.customSymbol))}
                  data-testid="dca-analyze-btn"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Analyser avec l'IA
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Scaling Tiers Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-primary" />
                  Paliers de Scaling
                </CardTitle>
                <CardDescription>
                  Configure les multiplicateurs en fonction de la baisse du prix
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {customTiers.map((tier, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">Baisse (%)</Label>
                        <Input
                          type="number"
                          value={tier.drop_percent}
                          onChange={(e) => updateTier(index, 'drop_percent', e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <div className="flex items-center justify-center pt-5">
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">Multiplicateur</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={tier.multiplier}
                          onChange={(e) => updateTier(index, 'multiplier', e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTier(index)}
                        disabled={customTiers.length <= 1}
                        className="mt-5"
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addTier} className="w-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un palier
                  </Button>
                </div>

                {/* Preview */}
                <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-primary" />
                    Exemple avec base de $100
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Prix initial</span>
                      <span>$100</span>
                    </div>
                    {customTiers.map((tier, index) => (
                      <div key={index} className="flex justify-between text-green-400">
                        <span>-{tier.drop_percent}% → </span>
                        <span className="font-semibold">${(100 * tier.multiplier).toFixed(0)} (x{tier.multiplier})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Analysis Tab */}
        <TabsContent value="analysis">
          {analysis && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-muted/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Prix Actuel</p>
                        <p className="text-2xl font-bold">${analysis.current_price?.toLocaleString()}</p>
                      </div>
                      <DollarSign className="w-8 h-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-muted/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Biais Marché</p>
                        <p className={`text-2xl font-bold ${
                          analysis.market_bias === 'bullish' ? 'text-green-400' :
                          analysis.market_bias === 'bearish' ? 'text-red-400' : 'text-yellow-400'
                        }`}>
                          {analysis.market_bias === 'bullish' ? 'Haussier' :
                           analysis.market_bias === 'bearish' ? 'Baissier' : 'Neutre'}
                        </p>
                      </div>
                      {analysis.market_bias === 'bullish' ? (
                        <TrendingUp className="w-8 h-8 text-green-400" />
                      ) : analysis.market_bias === 'bearish' ? (
                        <TrendingDown className="w-8 h-8 text-red-400" />
                      ) : (
                        <BarChart3 className="w-8 h-8 text-yellow-400" />
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-muted/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Prix Moyen Projeté</p>
                        <p className="text-2xl font-bold text-green-400">
                          ${analysis.improved_avg_price?.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          vs Classique: ${analysis.classic_avg_price?.toLocaleString()}
                        </p>
                      </div>
                      <Target className="w-8 h-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-green-500/10 border-green-500/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Économie Potentielle</p>
                        <p className="text-2xl font-bold text-green-400">
                          {analysis.potential_savings_percent?.toFixed(1)}%
                        </p>
                        <p className="text-xs text-green-400/70">vs DCA classique</p>
                      </div>
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* AI Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    Analyse IA - {analysis.symbol}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{analysis.ai_analysis}</p>
                </CardContent>
              </Card>

              {/* Budget Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PiggyBank className="w-5 h-5 text-primary" />
                    Ton Plan d'Investissement Concret
                  </CardTitle>
                  <CardDescription>
                    Voici exactement quand et combien investir - tu n'as plus qu'à suivre !
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Classic DCA Schedule */}
                    <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-blue-400 flex items-center gap-2">
                          <Calendar className="w-5 h-5" />
                          DCA Classique
                        </h4>
                        <Badge className="bg-blue-500/20 text-blue-400">
                          ${analysis.classic_budget?.toLocaleString()} total
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Investis <strong className="text-blue-400">${analysis.base_amount?.toFixed(2)}</strong> chaque{' '}
                        {analysis.frequency === 'daily' ? 'jour' : 
                         analysis.frequency === 'weekly' ? 'semaine' : 'mois'}, 
                        peu importe le prix.
                      </p>
                      
                      {/* Schedule table */}
                      <div className="bg-background/50 rounded-lg overflow-hidden">
                        <div className="grid grid-cols-3 gap-2 p-2 bg-blue-500/10 text-xs font-semibold text-blue-400">
                          <span>Date</span>
                          <span className="text-right">Montant</span>
                          <span className="text-right">Cumulé</span>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {analysis.classic_schedule?.map((entry, index) => (
                            <div key={index} className="grid grid-cols-3 gap-2 p-2 text-sm border-b border-border/30 last:border-0">
                              <span className="text-muted-foreground">{entry.date}</span>
                              <span className="text-right font-mono">${entry.amount?.toFixed(2)}</span>
                              <span className="text-right font-mono text-blue-400">${entry.cumulative?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        {analysis.classic_schedule?.length >= 12 && (
                          <div className="p-2 text-xs text-center text-muted-foreground bg-muted/30">
                            ... et ainsi de suite chaque {analysis.frequency === 'weekly' ? 'semaine' : analysis.frequency === 'daily' ? 'jour' : 'mois'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Improved DCA Schedule */}
                    <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-green-400 flex items-center gap-2">
                          <Zap className="w-5 h-5" />
                          DCA Amélioré
                        </h4>
                        <Badge className="bg-green-500/20 text-green-400">
                          ${analysis.improved_budget?.toLocaleString()} réservé
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Investis <strong className="text-green-400">plus</strong> quand le prix atteint ces niveaux attractifs.
                        Ce budget est réservé et se déclenche automatiquement.
                      </p>
                      
                      {/* Trigger conditions */}
                      <div className="bg-background/50 rounded-lg overflow-hidden">
                        <div className="grid grid-cols-12 gap-2 p-2 bg-green-500/10 text-xs font-semibold text-green-400">
                          <span className="col-span-5">Si le prix atteint</span>
                          <span className="col-span-2 text-center">Baisse</span>
                          <span className="col-span-2 text-center">Multi.</span>
                          <span className="col-span-3 text-right">Montant</span>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {analysis.improved_schedule?.map((entry, index) => {
                            // Determine zone styling based on type
                            let rowBg = '';
                            let zoneBadge = null;
                            
                            // Check if this is an OTE zone (61.8% or 78.6% Fibonacci)
                            const isOTEZone = entry.reason?.includes('OTE') || entry.reason?.includes('61.8') || entry.reason?.includes('78.6');
                            const isBestZone = entry.zone_type === 'strong_buy' && entry.recommended_multiplier >= 4;
                            
                            if (isOTEZone || isBestZone) {
                              // Special styling for OTE zones - most attractive
                              rowBg = 'bg-gradient-to-r from-green-500/20 via-emerald-500/15 to-green-500/20 border-l-4 border-l-emerald-400 animate-pulse-subtle';
                              zoneBadge = (
                                <div className="flex items-center gap-1">
                                  <span className="text-lg">⭐</span>
                                  <Badge className="bg-gradient-to-r from-emerald-500 to-green-500 text-white text-[10px] font-bold shadow-lg shadow-emerald-500/30">
                                    ZONE OTE
                                  </Badge>
                                </div>
                              );
                            } else if (entry.zone_type === 'strong_buy') {
                              rowBg = 'bg-green-500/10 border-l-2 border-l-green-500';
                              zoneBadge = <Badge className="bg-green-500/20 text-green-400 text-[10px]">OPTIMAL</Badge>;
                            } else if (entry.zone_type === 'intermediate') {
                              rowBg = 'bg-yellow-500/5 border-l-2 border-l-yellow-500/50';
                              zoneBadge = <Badge className="bg-yellow-500/20 text-yellow-400 text-[10px]">INTER.</Badge>;
                            } else {
                              rowBg = 'bg-blue-500/5';
                              zoneBadge = <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">ZONE</Badge>;
                            }
                            
                            const isOTEZoneRow = isOTEZone || isBestZone;
                            
                            return (
                              <div key={index} className={`grid grid-cols-12 gap-2 p-2 text-sm border-b border-border/30 last:border-0 ${rowBg}`}>
                                <div className="col-span-5 flex items-center gap-2">
                                  {zoneBadge}
                                  <span className="text-muted-foreground text-xs truncate">{entry.trigger_condition}</span>
                                </div>
                                <span className="col-span-2 text-center">
                                  <Badge variant="outline" className={`text-xs ${isOTEZoneRow ? 'text-emerald-400 border-emerald-400/50 bg-emerald-500/10' : 'text-red-400 border-red-400/30'}`}>
                                    -{entry.drop_percent}%
                                  </Badge>
                                </span>
                                <span className="col-span-2 text-center">
                                  <Badge className={`text-xs ${isOTEZoneRow ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold' : entry.zone_type === 'strong_buy' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                    x{entry.multiplier}
                                  </Badge>
                                </span>
                                <span className={`col-span-3 text-right font-mono font-bold ${isOTEZoneRow ? 'text-emerald-400 text-base' : entry.zone_type === 'strong_buy' ? 'text-green-400' : entry.zone_type === 'intermediate' ? 'text-yellow-400' : 'text-blue-400'}`}>
                                  ${entry.amount?.toFixed(0)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {/* Total row */}
                        {analysis.improved_schedule?.length > 0 && (
                          <div className="grid grid-cols-12 gap-2 p-2 bg-green-500/20 text-sm font-bold border-t-2 border-green-500/30">
                            <span className="col-span-9 text-green-400">TOTAL ALLOUÉ</span>
                            <span className="col-span-3 text-right font-mono text-green-400">
                              ${analysis.improved_schedule?.reduce((sum, e) => sum + (e.amount || 0), 0).toFixed(0)}
                            </span>
                          </div>
                        )}
                        {(!analysis.improved_schedule || analysis.improved_schedule.length === 0) && (
                          <div className="p-4 text-center text-muted-foreground text-sm">
                            Aucune zone d'achat identifiée
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-3 p-2 bg-yellow-500/10 rounded text-xs text-yellow-400 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Ces montants se déclenchent SI le prix atteint ces zones. Configure des alertes sur TradingView pour être notifié !</span>
                      </div>
                      
                      {/* Zone Legend */}
                      <div className="mt-3 p-3 bg-card/50 rounded-lg border border-white/5">
                        <p className="text-xs text-muted-foreground mb-2 font-medium">Légende des zones :</p>
                        <div className="flex flex-wrap gap-3 text-xs">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm">⭐</span>
                            <div className="w-3 h-3 rounded bg-gradient-to-r from-emerald-500/40 to-green-500/40 border-l-2 border-l-emerald-400"></div>
                            <span className="text-emerald-400 font-bold">ZONE OTE</span>
                            <span className="text-muted-foreground">- Fibonacci 61.8%-78.6% (Meilleur R:R)</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded bg-green-500/30 border-l-2 border-l-green-500"></div>
                            <span className="text-green-400">OPTIMAL</span>
                            <span className="text-muted-foreground">- Zones Fibonacci fortes</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded bg-blue-500/30"></div>
                            <span className="text-blue-400">ZONE</span>
                            <span className="text-muted-foreground">- Niveaux standards</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded bg-yellow-500/30 border-l-2 border-l-yellow-500/50"></div>
                            <span className="text-yellow-400">INTER.</span>
                            <span className="text-muted-foreground">- Zones intermédiaires</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Summary */}
                  <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-xs text-muted-foreground">Budget Total</p>
                        <p className="text-xl font-bold">${analysis.total_budget?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Prix Moyen Estimé</p>
                        <p className="text-xl font-bold text-green-400">${analysis.improved_avg_price?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">vs Prix Actuel</p>
                        <p className="text-xl font-bold text-green-400">-{analysis.potential_savings_percent}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Économie Potentielle</p>
                        <p className="text-xl font-bold text-green-400">
                          ${((analysis.current_price - analysis.improved_avg_price) * (analysis.total_budget / analysis.current_price)).toFixed(0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Save Plan Button */}
              <div className="flex justify-end">
                <Button variant="outline" className="mr-4" onClick={() => setActiveTab('setup')}>
                  Modifier les paramètres
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={saveDCAPlan}
                  disabled={savingPlan}
                  data-testid="save-dca-plan-btn"
                >
                  {savingPlan ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Sauvegarder ce plan
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Tracking Tab */}
        <TabsContent value="tracking">
          {!isAuthenticated ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Shield className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">Connecte-toi pour sauvegarder</h3>
                  <p className="text-sm mb-4">
                    Crée un compte pour sauvegarder et suivre tes plans DCA
                  </p>
                  <Button onClick={() => navigate('/subscribe')}>
                    S'inscrire
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : savedPlans.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <PiggyBank className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">Aucun plan actif</h3>
                  <p className="text-sm mb-4">
                    Crée et sauvegarde un plan DCA pour commencer à suivre tes investissements
                  </p>
                  <Button onClick={() => setActiveTab('setup')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer un plan
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Plan Selector */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-4 h-4 text-primary" />
                    Sélectionne un plan à suivre
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {savedPlans.map((plan) => (
                      <Button
                        key={plan.id}
                        variant={selectedPlanId === plan.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedPlanId(plan.id)}
                        className="flex items-center gap-2"
                      >
                        {plan.symbol}
                        <Badge variant="secondary" className="text-xs">
                          {new Date(plan.created_at).toLocaleDateString('fr-FR')}
                        </Badge>
                      </Button>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(selectedPlanId)}
                      className="text-red-400 hover:bg-red-500/10"
                      disabled={!selectedPlanId}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {confirmDeleteId && (
                      <div className="flex items-center gap-2 ml-2">
                        <span className="text-sm text-muted-foreground">Supprimer ?</span>
                        <Button size="sm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 h-7 px-2">Oui</Button>
                        <Button size="sm" variant="outline" onClick={cancelDelete} className="h-7 px-2">Non</Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {selectedPlan && (
                <>
                  {/* Comparison: AI Projection vs Reality */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* AI Projection */}
                    <Card className="border-primary/30">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Zap className="w-4 h-4 text-primary" />
                          Projection IA
                        </CardTitle>
                        <CardDescription>Ce que l'IA te recommande</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-3 bg-muted/30 rounded-lg">
                            <p className="text-xs text-muted-foreground">Budget Total</p>
                            <p className="text-xl font-bold">${selectedPlan.total_budget?.toLocaleString()}</p>
                          </div>
                          <div className="p-3 bg-muted/30 rounded-lg">
                            <p className="text-xs text-muted-foreground">Prix Moyen Projeté</p>
                            <p className="text-xl font-bold text-primary">${selectedPlan.improved_avg_price?.toLocaleString()}</p>
                          </div>
                          <div className="p-3 bg-blue-500/10 rounded-lg">
                            <p className="text-xs text-blue-400">DCA Classique</p>
                            <p className="font-semibold">${selectedPlan.classic_budget?.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">{selectedPlan.frequency === 'weekly' ? 'Hebdo' : selectedPlan.frequency === 'daily' ? 'Jour' : 'Mensuel'}</p>
                          </div>
                          <div className="p-3 bg-green-500/10 rounded-lg">
                            <p className="text-xs text-green-400">DCA Amélioré</p>
                            <p className="font-semibold">${selectedPlan.improved_budget?.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">{selectedPlan.improved_schedule?.length || 0} zones</p>
                          </div>
                        </div>

                        {/* Expandable AI Plan Details */}
                        <Accordion type="single" collapsible>
                          <AccordionItem value="ai-details" className="border-0">
                            <AccordionTrigger className="text-sm text-muted-foreground hover:text-foreground py-2 hover:no-underline">
                              <span className="flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                Voir les détails du plan IA
                              </span>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-4 pt-2">
                                {/* Classic Schedule */}
                                <div className="p-3 bg-blue-500/10 rounded-lg">
                                  <h5 className="font-semibold text-blue-400 mb-2 text-sm flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Planning DCA Classique
                                  </h5>
                                  <p className="text-xs text-muted-foreground mb-2">
                                    ${selectedPlan.base_amount?.toFixed(2)} / {selectedPlan.frequency === 'weekly' ? 'semaine' : selectedPlan.frequency === 'daily' ? 'jour' : 'mois'}
                                  </p>
                                  {selectedPlan.classic_schedule?.length > 0 ? (
                                    <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                                      {selectedPlan.classic_schedule?.slice(0, 8).map((entry, idx) => (
                                        <div key={idx} className="flex justify-between py-1 border-b border-border/30 last:border-0">
                                          <span className="text-muted-foreground">{entry.date}</span>
                                          <span className="font-mono">${entry.amount?.toFixed(2)}</span>
                                          <span className="text-blue-400 font-mono">${entry.cumulative?.toFixed(0)}</span>
                                        </div>
                                      ))}
                                      {selectedPlan.classic_schedule?.length > 8 && (
                                        <p className="text-muted-foreground text-center pt-1">... et {selectedPlan.classic_schedule.length - 8} de plus</p>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-muted-foreground">Pas de planning défini</p>
                                  )}
                                </div>

                                {/* Improved DCA Zones */}
                                <div className="p-3 bg-green-500/10 rounded-lg">
                                  <h5 className="font-semibold text-green-400 mb-2 text-sm flex items-center gap-2">
                                    <Target className="w-4 h-4" />
                                    Zones d'Achat DCA Amélioré
                                  </h5>
                                  {selectedPlan.improved_schedule?.length > 0 ? (
                                    <div className="space-y-2">
                                      {selectedPlan.improved_schedule?.map((zone, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-2 bg-background/50 rounded text-sm">
                                          <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="text-red-400 border-red-400/30 text-xs">
                                              -{zone.drop_percent}%
                                            </Badge>
                                            <span className="text-muted-foreground text-xs">
                                              {zone.trigger_condition || `Prix ≤ $${(selectedPlan.current_price * (1 - zone.drop_percent/100)).toFixed(2)}`}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">x{zone.multiplier}</Badge>
                                            <span className="font-bold text-green-400">${zone.amount?.toFixed(0)}</span>
                                          </div>
                                        </div>
                                      ))}
                                      <div className="flex justify-between pt-2 border-t border-green-500/30 text-sm font-bold">
                                        <span className="text-green-400">Total réservé</span>
                                        <span className="text-green-400">
                                          ${selectedPlan.improved_schedule?.reduce((sum, z) => sum + (z.amount || 0), 0).toFixed(0)}
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-muted-foreground">Aucune zone d'achat définie</p>
                                  )}
                                </div>

                                {/* AI Analysis */}
                                {selectedPlan.ai_analysis && (
                                  <div className="p-3 bg-muted/30 rounded-lg">
                                    <h5 className="font-semibold text-sm mb-1 flex items-center gap-2">
                                      <Zap className="w-4 h-4 text-primary" />
                                      Analyse IA
                                    </h5>
                                    <p className="text-xs text-muted-foreground">{selectedPlan.ai_analysis}</p>
                                  </div>
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </CardContent>
                    </Card>

                    {/* Reality - Your Actual Purchases with Live Price */}
                    <Card className="border-green-500/30">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-green-400" />
                            Réalité (Tes achats)
                          </span>
                          {planSummary?.current_price > 0 && (
                            <Badge className="bg-primary/20 text-primary font-mono">
                              {selectedPlan.symbol?.split('/')[0]}: ${planSummary.current_price?.toLocaleString()}
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription>Ce que tu as réellement acheté</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {loadingEntries ? (
                          <div className="flex items-center justify-center py-8">
                            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                          </div>
                        ) : planSummary && planSummary.total_invested > 0 ? (
                          <div className="space-y-4">
                            {/* P&L Banner */}
                            <div className={`p-4 rounded-lg ${planSummary.pnl_percent >= 0 ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-xs text-muted-foreground">Valeur Actuelle</p>
                                  <p className="text-2xl font-bold">
                                    ${(planSummary.total_quantity * planSummary.current_price)?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-muted-foreground">P&L</p>
                                  <p className={`text-2xl font-bold ${planSummary.pnl_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {planSummary.pnl_percent >= 0 ? '+' : ''}{planSummary.pnl_percent?.toFixed(2)}%
                                  </p>
                                  <p className={`text-sm ${planSummary.pnl_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {planSummary.pnl_amount >= 0 ? '+' : ''}${planSummary.pnl_amount?.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="p-3 bg-muted/30 rounded-lg">
                                <p className="text-xs text-muted-foreground">Total Investi</p>
                                <p className="text-xl font-bold">${planSummary.total_invested?.toLocaleString()}</p>
                              </div>
                              <div className="p-3 bg-muted/30 rounded-lg">
                                <p className="text-xs text-muted-foreground">Prix Moyen Réel</p>
                                <p className="text-xl font-bold text-green-400">${planSummary.average_entry_price?.toFixed(2)}</p>
                              </div>
                              <div className="p-3 bg-blue-500/10 rounded-lg">
                                <p className="text-xs text-blue-400">Classique</p>
                                <p className="font-semibold">${planSummary.classic_invested?.toLocaleString()}</p>
                                <p className="text-xs text-muted-foreground">{planSummary.classic_entries_count || 0} achats</p>
                              </div>
                              <div className="p-3 bg-green-500/10 rounded-lg">
                                <p className="text-xs text-green-400">Amélioré</p>
                                <p className="font-semibold">${planSummary.improved_invested?.toLocaleString()}</p>
                                <p className="text-xs text-muted-foreground">{planSummary.improved_entries_count || 0} achats</p>
                              </div>
                            </div>

                            <div className="p-3 bg-primary/10 rounded-lg">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="text-xs text-muted-foreground">Quantité Totale</p>
                                  <p className="font-bold text-lg">{planSummary.total_quantity?.toFixed(6)} {selectedPlan.symbol?.split('/')[0]}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-muted-foreground">Prix Actuel</p>
                                  <p className="font-bold text-lg text-primary">${planSummary.current_price?.toLocaleString()}</p>
                                </div>
                              </div>
                            </div>

                            {/* Refresh Button */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => fetchPlanEntries(selectedPlanId)}
                              className="w-full"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Rafraîchir le prix
                            </Button>
                          </div>
                        ) : (
                          <div className="text-center py-6 text-muted-foreground">
                            <p>Aucun achat enregistré</p>
                            <p className="text-sm">Ajoute tes achats ci-dessous</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Add Entry Form */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Plus className="w-4 h-4 text-primary" />
                        Ajouter un achat
                      </CardTitle>
                      <CardDescription>Enregistre un achat effectué sur ton exchange</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Date</Label>
                          <Input
                            type="date"
                            value={newEntry.entry_date}
                            onChange={(e) => setNewEntry({...newEntry, entry_date: e.target.value})}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Montant ($)</Label>
                          <Input
                            type="number"
                            placeholder="100"
                            value={newEntry.amount}
                            onChange={(e) => setNewEntry({...newEntry, amount: e.target.value})}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Prix d'achat ($)</Label>
                          <Input
                            type="number"
                            placeholder="65000"
                            step="0.01"
                            value={newEntry.price}
                            onChange={(e) => setNewEntry({...newEntry, price: e.target.value})}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Type DCA</Label>
                          <Select 
                            value={newEntry.entry_type}
                            onValueChange={(v) => setNewEntry({...newEntry, entry_type: v})}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="classic">
                                <span className="flex items-center gap-2">
                                  <Calendar className="w-3 h-3 text-blue-400" />
                                  Classique
                                </span>
                              </SelectItem>
                              <SelectItem value="improved">
                                <span className="flex items-center gap-2">
                                  <Zap className="w-3 h-3 text-green-400" />
                                  Amélioré
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">&nbsp;</Label>
                          <Button 
                            onClick={handleAddEntry}
                            disabled={addingEntry || !newEntry.amount || !newEntry.price}
                            className="w-full h-9"
                          >
                            {addingEntry ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-1" />
                                Ajouter
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      {newEntry.amount && newEntry.price && (
                        <p className="text-xs text-muted-foreground mt-2">
                          = {(parseFloat(newEntry.amount) / parseFloat(newEntry.price)).toFixed(6)} {selectedPlan.symbol?.split('/')[0]}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Entries Table */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <LineChart className="w-4 h-4 text-primary" />
                        Historique des achats
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {planEntries.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>Aucun achat enregistré pour ce plan</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border/50">
                                <th className="text-left py-2 px-2 text-muted-foreground font-medium">Date</th>
                                <th className="text-right py-2 px-2 text-muted-foreground font-medium">Montant</th>
                                <th className="text-right py-2 px-2 text-muted-foreground font-medium">Prix</th>
                                <th className="text-right py-2 px-2 text-muted-foreground font-medium">Quantité</th>
                                <th className="text-center py-2 px-2 text-muted-foreground font-medium">Type</th>
                                <th className="text-right py-2 px-2 text-muted-foreground font-medium">Cumul</th>
                                <th className="py-2 px-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                let cumulativeAmount = 0;
                                let cumulativeQuantity = 0;
                                // Sort by date ascending for cumulative calculation
                                const sortedEntries = [...planEntries].sort((a, b) => 
                                  new Date(a.entry_date) - new Date(b.entry_date)
                                );
                                // Calculate cumulative and reverse for display
                                const entriesWithCumulative = sortedEntries.map(entry => {
                                  cumulativeAmount += entry.amount;
                                  cumulativeQuantity += entry.quantity;
                                  return { ...entry, cumulativeAmount, cumulativeQuantity };
                                }).reverse();
                                
                                return entriesWithCumulative.map((entry) => (
                                  <tr key={entry.id} className="border-b border-border/30 hover:bg-muted/20">
                                    <td className="py-2 px-2">
                                      {new Date(entry.entry_date).toLocaleDateString('fr-FR')}
                                    </td>
                                    <td className="text-right py-2 px-2 font-mono">
                                      ${entry.amount?.toFixed(2)}
                                    </td>
                                    <td className="text-right py-2 px-2 font-mono">
                                      ${entry.price?.toLocaleString()}
                                    </td>
                                    <td className="text-right py-2 px-2 font-mono text-primary">
                                      {entry.quantity?.toFixed(6)}
                                    </td>
                                    <td className="text-center py-2 px-2">
                                      <Badge className={
                                        entry.entry_type === 'classic' 
                                          ? 'bg-blue-500/20 text-blue-400' 
                                          : 'bg-green-500/20 text-green-400'
                                      }>
                                        {entry.entry_type === 'classic' ? 'Classique' : 'Amélioré'}
                                      </Badge>
                                    </td>
                                    <td className="text-right py-2 px-2">
                                      <div className="text-xs">
                                        <span className="font-mono">${entry.cumulativeAmount?.toFixed(0)}</span>
                                      </div>
                                    </td>
                                    <td className="py-2 px-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteEntry(entry.id)}
                                        disabled={deletingEntryId === entry.id}
                                        className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                      >
                                        {deletingEntryId === entry.id ? (
                                          <RefreshCw className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <Trash2 className="w-3 h-3" />
                                        )}
                                      </Button>
                                    </td>
                                  </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          )}
        </TabsContent>

        {/* History Tab - Triggered Zone Alerts */}
        <TabsContent value="history">
          <div className="space-y-6">
            {/* Header */}
            <Card className="glass border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center">
                      <Bell className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Historique des Alertes DCA</CardTitle>
                      <CardDescription>Zones d'achat atteintes par vos plans</CardDescription>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchZoneHistory}
                    disabled={loadingHistory}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Zone History List */}
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : zoneHistory.length > 0 ? (
              <div className="space-y-4">
                {zoneHistory.map((entry, index) => {
                  const isOTE = entry.is_ote || entry.zone_type === 'strong_buy';
                  const triggeredDate = new Date(entry.triggered_at);
                  const formattedDate = triggeredDate.toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  
                  return (
                    <Card 
                      key={`${entry.plan_id}-${entry.zone_id}-${index}`}
                      className={`glass border-white/10 overflow-hidden ${isOTE ? 'border-l-4 border-l-emerald-400' : ''}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {/* Icon */}
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              isOTE 
                                ? 'bg-gradient-to-br from-emerald-500/30 to-green-500/30' 
                                : 'bg-blue-500/20'
                            }`}>
                              {isOTE ? (
                                <span className="text-2xl">⭐</span>
                              ) : (
                                <Target className="w-6 h-6 text-blue-400" />
                              )}
                            </div>
                            
                            {/* Info */}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-white">
                                  {getAssetName(entry.symbol) || entry.symbol}
                                </span>
                                {isOTE && (
                                  <Badge className="bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs">
                                    ZONE OTE
                                  </Badge>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  x{entry.multiplier}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-400 mt-1">
                                {entry.zone_reason}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formattedDate}
                                </span>
                                <span>
                                  Zone: ${entry.zone_start?.toLocaleString()} - ${entry.zone_end?.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Price at trigger */}
                          <div className="text-right">
                            <p className="text-xs text-gray-500">Prix à l'alerte</p>
                            <p className={`text-lg font-bold ${isOTE ? 'text-emerald-400' : 'text-blue-400'}`}>
                              ${entry.price_at_trigger?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="glass border-white/10">
                <CardContent className="py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center mx-auto mb-4">
                    <Bell className="w-8 h-8 text-gray-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-300 mb-2">Aucune alerte DCA</h3>
                  <p className="text-gray-500 text-sm max-w-md mx-auto">
                    Les alertes apparaîtront ici lorsque le prix d'un actif entrera dans une zone d'achat de vos plans DCA sauvegardés.
                  </p>
                  <div className="mt-6 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20 max-w-sm mx-auto">
                    <p className="text-emerald-400 text-sm flex items-center justify-center gap-2">
                      <Info className="w-4 h-4" />
                      Surveillé toutes les 20 minutes
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Info Card */}
            <Card className="glass border-white/10">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div className="text-sm text-gray-400">
                    <p className="font-medium text-gray-300 mb-1">Comment ça fonctionne ?</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Le système surveille vos plans DCA toutes les 20 minutes</li>
                      <li>Quand le prix entre dans une zone d'achat, vous recevez une notification push</li>
                      <li>Les zones OTE (⭐) sont les meilleures opportunités d'achat (61.8%-78.6% Fibonacci)</li>
                      <li>Chaque zone ne déclenche qu'une seule alerte pour éviter le spam</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DCA;
